import { Divider, Loader } from 'components';
import { AnimatePresence } from 'framer-motion';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Arrow, useLayer } from 'react-laag';
import { PDFIcon, ShareIcon } from 'static';

import Tooltip from '../Tooltip';
import {
  StyledLoaderWrapper,
  StyledOption,
  StyledThreeDots,
  StyledTooltip,
} from './styled';

interface EventShareOptionsTooltipProps {
  zoom?: number;
  onShareClick: (e: Event) => void;
  onExportPDFClick: () => void | null;
}

const EventShareOptionsTooltip: FC<EventShareOptionsTooltipProps> = ({
  zoom = 1,
  onShareClick,
  onExportPDFClick,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const closeTooltip = () => setIsVisible(false);

  const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
    isOpen: isVisible,
    placement: 'right-start',
    triggerOffset: 10,
    auto: true,
    onOutsideClick: closeTooltip,
  });

  const handleIconClick = (e) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  const tooltipProps = {
    zoom,
    tooltipStyle: {
      fontSize: 12 * zoom,
      maxWidth: 200 * zoom,
      whiteSpace: 'pre-wrap',
      lineBreak: 'normal',
    },
    opacity: '0.9',
    text: t('event.shareEvent'),
  };

  const handlePDFExport = async (e) => {
    e.stopPropagation();
    setLoading(true);
    await onExportPDFClick();
    setLoading(false);
    setIsVisible(false);
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    onShareClick(e);
    setIsVisible(false);
  };

  return (
    <>
      <div {...triggerProps}>
        <StyledThreeDots zoom={zoom} onClick={handleIconClick}>
          ...
        </StyledThreeDots>
      </div>
      {renderLayer(
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment*/
        /* @ts-ignore*/
        <AnimatePresence>
          {isVisible && (
            <StyledTooltip
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              {...layerProps}
              style={{
                ...layerProps.style,
              }}
              zoom={zoom}
            >
              <Arrow {...arrowProps} size={5} />
              <div>
                {loading && (
                  <StyledLoaderWrapper>
                    <Loader size="30px" type={null} />
                  </StyledLoaderWrapper>
                )}
                <Tooltip {...tooltipProps} position="top-center">
                  <StyledOption zoom={zoom} onClick={handleShareClick}>
                    <ShareIcon />
                    <label>{t('event.shareEventOption')}</label>
                  </StyledOption>
                </Tooltip>
                {!!onExportPDFClick && (
                  <>
                    <Divider customMargin="5px 0px" />
                    <StyledOption zoom={zoom} onClick={handlePDFExport}>
                      <PDFIcon />
                      <label>{t('event.exportPDFOption')}</label>
                    </StyledOption>
                  </>
                )}
              </div>
            </StyledTooltip>
          )}
        </AnimatePresence>,
      )}
    </>
  );
};

export default EventShareOptionsTooltip;
